/**
 * @file Button.tsx
 * @description Shared button primitive wrapping the existing .bybit-btn-primary /
 * .bybit-btn-secondary CSS classes. Renders a <Link> when `to` is given, otherwise
 * a native <button>, so the same component covers both actions and navigation.
 */
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Link, type LinkProps } from 'react-router-dom';

export type ButtonVariant = 'primary' | 'secondary';

interface ButtonOwnProps {
  variant?: ButtonVariant;
  children: ReactNode;
}

type ButtonAsButton = ButtonOwnProps &
  ButtonHTMLAttributes<HTMLButtonElement> & {
    to?: undefined;
  };

type ButtonAsLink = ButtonOwnProps &
  Omit<LinkProps, 'className'> & {
    to: LinkProps['to'];
    className?: string;
  };

export type ButtonProps = ButtonAsButton | ButtonAsLink;

const variantClass: Record<ButtonVariant, string> = {
  primary: 'bybit-btn-primary',
  secondary: 'bybit-btn-secondary',
};

export const Button = ({ variant = 'primary', className, children, ...rest }: ButtonProps): JSX.Element => {
  const composedClassName = [variantClass[variant], className].filter(Boolean).join(' ');

  if ('to' in rest && rest.to !== undefined) {
    const { to, ...linkRest } = rest as Omit<ButtonAsLink, keyof ButtonOwnProps | 'className'>;
    return (
      <Link to={to} className={composedClassName} {...linkRest}>
        {children}
      </Link>
    );
  }

  const buttonRest = rest as Omit<ButtonAsButton, keyof ButtonOwnProps | 'className' | 'to'>;
  return (
    <button className={composedClassName} {...buttonRest}>
      {children}
    </button>
  );
};
